import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';
import './HomePage.css';
import logo from '../logo.PNG';

const HomePage = () => {
  const navigate = useNavigate();
  const [showPricing, setShowPricing] = useState(false);
  const spotlightRef = useRef(null);
  const logoRef = useRef(null);
  const leftBeamRef = useRef(null);
  const rightBeamRef = useRef(null);
  const diagonalLeftRef = useRef(null);
  const diagonalRightRef = useRef(null); 
  const spotlight1Ref = useRef(null);
  const spotlight2Ref = useRef(null);

  useEffect(() => {
    // First spotlight sequence
    setTimeout(() => {
      spotlight1Ref.current?.classList.add('active');
    }, 200);

    setTimeout(() => {
      spotlight2Ref.current?.classList.add('active');
    }, 1000);

    // Original animation sequence with delayed timing
    setTimeout(() => {
      diagonalLeftRef.current?.classList.add('active');
    }, 2000);

    setTimeout(() => {
      diagonalRightRef.current?.classList.add('active');
    }, 2400);

    setTimeout(() => {
      leftBeamRef.current?.classList.add('active');
    }, 2800);

    setTimeout(() => {
      rightBeamRef.current?.classList.add('active');
    }, 3200);

    setTimeout(() => {
      spotlightRef.current?.classList.add('active');
    }, 3600);

    setTimeout(() => {
      logoRef.current?.classList.add('fully-illuminated');
      document.querySelector('.content')?.classList.add('content-animated');
    }, 4000);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px'
      }
    );

    const cards = document.querySelectorAll('.service-card');
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Check if user is logged in when component mounts
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    setShowPricing(isLoggedIn);
  }, []);

  useEffect(() => {
    // Add visibility class to pricing tiers after login
    if (showPricing) {
      const pricingElements = document.querySelectorAll('.pricing-tiers');
      pricingElements.forEach(el => {
        setTimeout(() => {
          el.classList.add('visible');
        }, 300);
      });
    }
  }, [showPricing]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('visible');
            }, entry.target.dataset.delay || 0);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px' // Consolidated rootMargin
      }
    );

    // Observe all intro and service cards with different delays
    const allCards = document.querySelectorAll('.intro-card, .service-card');
    allCards.forEach((card, index) => {
      card.dataset.delay = index * 200; // Stagger the animations
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const handleLearnMore = () => {
    window.location.href = '#about'; // Scroll to login form
  };

  const handleLogin = (e) => {
    e.preventDefault();
    localStorage.setItem('isLoggedIn', 'true');
    setShowPricing(true);
    window.location.href = '#services';
    window.location.reload(); // Add this line to ensure pricing updates
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    navigate('/auth', { state: { mode: 'reset' } });
  };

  const handleCreateAccount = (e) => {
    e.preventDefault();
    navigate('/auth', { state: { mode: 'signup' } });
  };

  const handleLogout = (e) => {
    e.preventDefault();
    localStorage.removeItem('isLoggedIn');
    setShowPricing(false);
    window.location.href = '#services';
    window.location.reload();
  };

  const handleBooking = (service, tier, price) => {
    if (!localStorage.getItem('isLoggedIn')) {
      handleLearnMore();
      return;
    }
    navigate('/booking', { state: { service, tier, price } });
  };

  const renderPricing = (prices, service) => {
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    
    if (!isLoggedIn) {
      return (
        <button className="learn-more-btn pulse-effect" onClick={handleLearnMore}>
          Learn More
        </button>
      );
    }

    return (
      <div className="pricing-tiers slide-in">
        <div className="price-tier" onClick={() => handleBooking(service, 'Basic', prices.basic)}>
          <h4>Basic</h4>
          <span className="price">{prices.basic}</span>
        </div>
        <div className="price-tier" onClick={() => handleBooking(service, 'Standard', prices.standard)}>
          <h4>Standard</h4>
          <span className="price">{prices.standard}</span>
        </div>
        <div className="price-tier" onClick={() => handleBooking(service, 'Premium', prices.premium)}>
          <h4>Premium</h4>
          <span className="price">{prices.premium}</span>
        </div>
      </div>
    );
  };

  const handleClick = (event) => {
    event.preventDefault();
    // Add your click handling logic here
  };

  const renderServiceCard = (service, description, prices) => (
    <div className="service-card">
      <img src={logo} alt={service} className="service-icon" />
      <h3>{service}</h3>
      <p>{description}</p>
      {renderPricing(prices, service)}
    </div>
  );

  return (
    <div className="home-container">
      <section id="home" className="section">
        <div className="logo-spotlight-container">
          <div className="spotlight-beam spotlight1" ref={spotlight1Ref}></div>
          <div className="spotlight-beam spotlight2" ref={spotlight2Ref}></div>
          <div className="diagonal-beam diagonal-left" ref={diagonalLeftRef}></div>
          <div className="diagonal-beam diagonal-right" ref={diagonalRightRef}></div>
          <div className="corner-beam left-beam" ref={leftBeamRef}></div>
          <div className="corner-beam right-beam" ref={rightBeamRef}></div>
          <div className="spotlight" ref={spotlightRef}></div>
          
          <div className="logo-container" ref={logoRef}>
            <div className="flash"></div>
            <div className="circular-text">
              {Array.from("FALCONS  • DIGITAL SOLUTIONS • ").map((letter, i, arr) => (
                <span
                  key={i}
                  className="text-letter"
                  style={{
                    transform: `rotate(${i * (360 / arr.length)}deg)`
                  }}
                >
                  {letter}
                </span>
              ))}
            </div>
            <img src={logo} alt="Falcons Logo" className="logo" />
            <div className="logo-text">FALCONS</div>
            <div className="logo-subtitle">MARKETING COMPANY</div>
          </div>
        </div>
      </section>

      {/* Intro Section - Hidden */}
      {/* <section id="intro" className="section">
        <div className="intro-container">
          <h2>Transform Your Digital Presence</h2>
          <div className="intro-content">
            <div className="intro-card">
              <h3>Who We Are</h3>
              <p>We are a dynamic marketing agency dedicated to elevating brands through innovative digital solutions and strategic excellence.</p>
            </div>
            <div className="intro-card">
              <h3>Our Mission</h3>
              <p>To empower businesses with cutting-edge marketing strategies that drive growth and create lasting impact in the digital landscape.</p>
            </div>
          </div>
        </div>
      </section> */}

      <section id="intro1" className="section">
        <div className="coming-soon-container">
          <h2 className="coming-soon-text">Coming Soon</h2>
        </div>
      </section>

      {/* Services Section - Hidden */}
      {/* <section id="services" className="section">
        <h2>Our Services</h2>
        <div className="services-grid">
          {renderServiceCard("Digital Marketing", "Drive growth with data-driven strategies: SEO, PPC, email marketing, and analytics tailored to your business goals.", {
            basic: "$500/month",
            standard: "$1,000/month",
            premium: "$2,500/month"
          })}
          {renderServiceCard("Brand Strategy", "Define your brand identity, values, and positioning with a cohesive strategy that connects with your audience.", {
            basic: "$1,000",
            standard: "$2,500",
            premium: "$5,000"
          })}
          {renderServiceCard("Content Creation", "Engage your audience with high-quality blogs, videos, and infographics that tell your brand's story.", {
            basic: "$300/month",
            standard: "$700/month",
            premium: "$1,500/month"
          })}
          {renderServiceCard("Social Media", "Boost your social presence with expert management, content planning, and performance tracking.", {
            basic: "$400/month",
            standard: "$800/month",
            premium: "$1,500/month"
          })}
        </div>
      </section> */}

      {/* About Section - Hidden */}
      {/* <section id="about" className="section">
        <h2>Account</h2>
        <div className="login-form">
          {!showPricing ? (
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <input type="email" placeholder="Email" className="form-input" required />
              </div>
              <div className="form-group">
                <input type="password" placeholder="Password" className="form-input" required />
              </div>
              <button type="submit" className="cta-button">Login</button>
              <div className="login-options">
                <button onClick={handleForgotPassword} className="forgot-password">
                  Forgot Password?
                </button>
                <span className="divider">|</span>
                <button onClick={handleCreateAccount} className="create-account">
                  Create Account
                </button>
              </div>
            </form>
          ) : (
            <div>
              <p style={{ color: '#000', marginBottom: '1rem' }}>You are logged in</p>
              <button onClick={handleLogout} className="cta-button">
                Logout
              </button>
            </div>
          )}
        </div>
      </section> */}

      <section id="contact" className="section">
        <h2>Contact Us</h2>
        <div className="contact-form">
          <input type="email" placeholder="Your Email" />
          <textarea placeholder="Your Message"></textarea>
          <button className="cta-button">Send Message</button>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-content">
          <div className="map-container">
            <iframe
              title="Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3610.178767169755!2d55.2721834!3d25.2048493!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjXCsDEyJzE3LjUiTiA1NcKwMTYnMTkuOSJF!5e0!3m2!1sen!2sae!4v1635444243645!5m2!1sen!2sae"
              width="100%"
              height="300"
              style={{ border: 0, borderRadius: '10px' }}
              allowFullScreen=""
              loading="lazy"
            ></iframe>
          </div>
          <div className="social-links">
            <a href="https://www.facebook.com/share/16mw93cTM6/?mibextid=wwXIfr" className="social-icon" title="Facebook" target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon icon={faFacebook} />
            </a>
            <a href="https://www.instagram.com/falcons_marketing/" className="social-icon" title="Instagram" target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon icon={faInstagram} />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
